import {spotheroPatch} from 'api';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {type RawApiResponse} from 'api/api.types';
import {type MonthlyRate} from 'api/rates/rates.types';

interface UpdateMonthlyRentalRulesParams {
    facilityId: number;
    ruleId: number;
    maxQuantity: number;
}

type UpdateMonthlyRentalRulesResponse = MonthlyRate;

type UpdateMonthlyRentalRulesRawResponse =
    RawApiResponse<UpdateMonthlyRentalRulesResponse>;

/**
 * Formats the request object for updating monthly rental rules.
 *
 */
const formatUpdateMonthlyRentalRulesRequest = (
    request: Pick<UpdateMonthlyRentalRulesParams, 'maxQuantity'>
) => {
    const formattedRequest = convertKeysToSnakeCase(request);

    return formattedRequest;
};

/**
 * Formats the response object for updating monthly rental rules.
 * @param response
 */
const formatUpdateMonthlyRentalRulesResponse = (
    response: UpdateMonthlyRentalRulesRawResponse
) => {
    const formattedResponse = camelizeKeys(response);

    return formattedResponse;
};

/**
 * Updates a given faciliity's inventory with a new default number of available spaces
 * @param facilityId - The facility id.
 * @param ruleId - The rule id.
 * @param maxQuantity - remaining_inventory for non-recurring and total_inventory for recurring inventory
 *
 * @see '/api/v1/facilities/{facilityId}/monthly-rental-rules/{ruleId}/'
 */
const updateMonthlyRentalRules = async ({
    facilityId,
    ruleId,
    maxQuantity,
}: UpdateMonthlyRentalRulesParams) => {
    try {
        const formattedRequest = formatUpdateMonthlyRentalRulesRequest({
            maxQuantity,
        });

        const response =
            await spotheroPatch<UpdateMonthlyRentalRulesRawResponse>(
                `facilities/${facilityId}/monthly-rental-rules/${ruleId}/`,
                formattedRequest
            );

        const formattedResponse = formatUpdateMonthlyRentalRulesResponse(
            response.data.data
        );

        return formattedResponse?.maxQuantity;
    } catch (err) {
        sendSentryException(err);

        throw err;
    }
};

export default updateMonthlyRentalRules;
